import type { MutableRefObject } from 'react'

import { useNotification } from 'app/providers'
import { ALERT_CONSTANTS } from 'common/constants/alertConstants'

import { useReplaceAppointmentMedicalIssuesMutation } from 'features/Schedule/api/medicalIssues.api'
import { getIssueErrorMessage } from 'features/Schedule/api/medicalIssues.transform'

import { type AppointmentIssuesSelection, EMPTY_ISSUES_SELECTION } from './issuePicker.types'

const NOTIFICATION_TITLE = 'Medical issues'
const SAVE_FAILED = 'Could not save what this visit is for. Please try again.'

type UseSaveAppointmentIssuesArgs = {
  siteId?: string
  selectionRef: MutableRefObject<AppointmentIssuesSelection>
}

type SaveAppointmentIssues = (appointmentId?: number | null) => Promise<void>

type UseSaveAppointmentIssuesResult = {
  saveAppointmentIssues: SaveAppointmentIssues
  saveCreatedAppointmentIssues: SaveAppointmentIssues
}

// Replaces the booking's whole set once the appointment itself is saved.
export const useSaveAppointmentIssues = ({
  siteId,
  selectionRef,
}: UseSaveAppointmentIssuesArgs): UseSaveAppointmentIssuesResult => {
  const { setNotification } = useNotification()
  const [replaceAppointmentIssues] = useReplaceAppointmentMedicalIssuesMutation()

  const saveAppointmentIssues: SaveAppointmentIssues = async (appointmentId) => {
    const selection = selectionRef.current
    if (!appointmentId || !siteId || !selection.dirty) return

    try {
      await replaceAppointmentIssues({
        appointmentId,
        siteId,
        data: { issue_ids: selection.issueIds, procedure_code_ids: selection.procedureCodeIds },
      }).unwrap()
    } catch (error) {
      setNotification({
        title: NOTIFICATION_TITLE,
        description: getIssueErrorMessage(error, SAVE_FAILED),
        type: ALERT_CONSTANTS.ERROR,
      })
    } finally {
      selectionRef.current = EMPTY_ISSUES_SELECTION
    }
  }

  // Problems travel in the booking's POST body and reach the EMR with the background sync.
  // The EMR copy does not exist yet right after create, so only procedures justify the replace.
  const saveCreatedAppointmentIssues: SaveAppointmentIssues = async (appointmentId) => {
    if (selectionRef.current.procedureCodeIds.length) return saveAppointmentIssues(appointmentId)
    selectionRef.current = EMPTY_ISSUES_SELECTION
  }

  return { saveAppointmentIssues, saveCreatedAppointmentIssues }
}
